import { login, setGoogleLoginData } from 'actions/auth';
import analytics from 'analytics';
import { Api } from 'api';
import {
  Page,
  Button,
  Form,
  Input,
  PageCard,
  toaster
} from 'components/common';
import FormValidator from 'components/FormValidator';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useLoadUserContext from 'providers/useLoadUserContext';
import localforage from 'localforage';
import { removeAuthToken } from 'config/settings';
import CleverLoginButton from 'components/Auth/CleverLoginButton';
import jwt_decode from 'jwt-decode';
import GoogleLoginButton from 'components/Auth/GoogleLoginButton';
import useQueryParams from 'hooks/useQueryParams';

const Login = () => {
  const loadUserContext = useLoadUserContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useQueryParams();
  const { user, redirectUri } = params;
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    email: user ?? '',
    password: ''
  });

  useEffect(() => {
    removeAuthToken();
    sessionStorage.clear();
    localforage.clear();
  }, []);

  const failWithError = message => {
    toaster.danger(message);
    analytics.track('Login Failed', { category: 'Login', label: message });
    setLoading(false);
  };

  const handleChange = e => {
    let { name, value } = e.target;
    if (name === 'email') {
      value = value.trim();
    }
    setState(() => ({
      ...state,
      [name]: value
    }));
  };

  const responseGoogle = async response => {
    if (loading) return;
    if (!response?.credential) {
      failWithError('Invalid Google response');
      return;
    }
    setLoading(true);
    analytics.track('Google Login Clicked', { category: 'Login' });

    try {
      const token = await Api.signInWithGoogle(response.credential);

      if (!token) {
        const claims = jwt_decode(response.credential);
        dispatch(
          setGoogleLoginData({
            googleToken: response.credential,
            firstName: claims.given_name,
            lastName: claims.family_name,
            email: claims.email
          })
        );
        navigate('/signup');
        return;
      }

      analytics.track('Google Login', { category: 'Login' });
      handleLoginSuccess(token);
    } catch (e) {
      failWithError(e.message);
    }
  };

  const handleLogin = async (validate, e) => {
    e.preventDefault();

    if (loading || !validate()) return;

    setLoading(true);
    const { email, password } = state;
    try {
      const token = await Api.signIn(email, password);
      handleLoginSuccess(token);
    } catch (e) {
      failWithError(e.message);
    }
  };

  const handleLoginSuccess = async refreshToken => {
    dispatch(login(refreshToken));
    await loadUserContext();
    navigate(redirectUri ? `/${redirectUri}` : '/');
    setLoading(false);
  };

  const { email, password } = state;

  return (
    <Page title="Login" container="xs" style={{ zIndex: 999 }}>
      <PageCard className="fadeInAndDown">
        <PageCard.Header>
          <h1>Log in to Eedi</h1>
        </PageCard.Header>
        <PageCard.Body className="fadeInAndDown2">
          <FormValidator
            fields={{ email, password }}
            rules={{
              email: val =>
                !val && 'Please enter your email address or username',
              password: val => !val && 'Please enter your password'
            }}>
            {(_, validate) => (
              <Form onSubmit={e => handleLogin(validate, e)} loading={loading}>
                <Form.Group>
                  <Input
                    name="email"
                    label="Username"
                    value={email}
                    onChange={handleChange}
                    placeholder=""
                    type="text"
                    autoFocus
                  />
                </Form.Group>
                <Form.Group>
                  <ForgotLink to={'/login/forgot-password'} tabIndex="-1">
                    Forgot password
                  </ForgotLink>
                  <Input
                    name="password"
                    label="Password"
                    value={password}
                    onChange={handleChange}
                    type="password"
                    placeholder=""
                  />
                </Form.Group>
                <Button type="submit" color="primary" disabled={loading} block>
                  Log in
                </Button>
                <GoogleLogin>
                  <SplitWrapper>
                    <div className="split-line" />
                    <div className="split-copy">or</div>
                    <div className="split-line" />
                  </SplitWrapper>
                  <GoogleLoginButton callback={responseGoogle} />
                  <CleverLoginButton style={{ margin: '0.5rem 0 0 0' }} />
                </GoogleLogin>
              </Form>
            )}
          </FormValidator>
        </PageCard.Body>
      </PageCard>
    </Page>
  );
};

const ForgotLink = styled(Link)`
  float: right;
`;

const GoogleLogin = styled.div`
  margin-top: 1rem;
  text-align: center;
  color: var(--gray);
  span {
    display: block;
    margin-bottom: 0.75rem;
  }
`;

const SplitWrapper = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: flex-start;
  align-content: flex-start;
  margin-bottom: 1rem;
  .split-copy {
    margin: 0 1rem;
    color: var(--gray);
  }
  .split-line {
    position: relative;
    margin-top: 12px;
    border-bottom: 1px solid var(--gray-light);
    width: 100%;
  }
`;

export default Login;
